import { forwardRef, useCallback, useContext, useEffect, useState } from 'react'

import { faXmark, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button, Flex } from 'antd'
import styled from 'styled-components'

import useDisclosure from '../../../hooks/use-disclosure'
import { getModalRoot } from '../Portal/Portal'
import { T } from '../Text/Text'
import Tooltip from '../Tooltip/Tooltip'

const Overlay = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.colorBgMask};
  position: fixed;
  inset: 0;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes overlayShow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const AlignmentContainer = styled.div`
  position: fixed;
  top: 0;
  display: flex;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  pointer-events: none; // Allows clicks to fall through the margins on the side and trigger the Overlay onClick

  margin: 80px 0px;
  @media (max-width: 768px) {
    margin: 20px 0px;
  }

  animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes contentShow {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const Panel = styled(DialogPrimitive.Content)<{
  $height?: React.CSSProperties['height']
  $width?: React.CSSProperties['width']
}>`
  position: relative;
  background-color: ${({ theme }) => theme.colorWhite};
  border-radius: 6px;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};
  padding: 0;
  width: 100%;
  max-width: ${(props) => props.$width || '500px'};
  height: ${(props) => props.$height || 'fit-content'};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  pointer-events: auto;

  max-height: calc(100vh - 160px);
  @media (max-width: 768px) {
    max-height: calc(100vh - 40px);
  }

  &:focus {
    outline: none;
  }
`

const Header = styled.header`
  padding: 12px 16px;
  margin: 0;
  border-bottom: solid 1px ${({ theme }) => theme.colorSplit};
`

const Title = styled(DialogPrimitive.Title)`
  margin: 0;
  width: 100%;
`

const CloseButton = styled(DialogPrimitive.Close)`
  height: 24px;
  width: 24px;
  padding: 0;
  margin: 0;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colorWhite};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.1s;

  &:focus-visible {
    outline: solid 2px ${({ theme }) => theme.colorPrimaryBorder};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colorBgTextHover};
  }
`

const Main = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  height: 100%;
`

const Footer = styled.footer`
  padding: 8px 12px;
  border-top: solid 1px ${({ theme }) => theme.colorSplit};
  height: fit-content;
  min-height: fit-content;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;

  &:empty {
    padding: 0px;
  }
`

const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
  e.stopPropagation()
}

type Props = {
  children: React.ReactNode
  className?: string
  height?: React.CSSProperties['height']
  onClose: () => void
  /**
   * Customize which element receives focus when the modal closes; by default it is the element that triggered the modal, but you will need to set this if the trigger no longer exists (e.g. a menu item)
   */
  onCloseFocusRef?: React.RefObject<HTMLElement>
  width?: React.CSSProperties['width']
}

const Modal = ({ children, height, onClose, onCloseFocusRef, width, className }: Props) => {
  const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setLastFocusedElement(document.activeElement as HTMLElement)
  }, [])

  const handleAnimationEnd = useCallback(() => {
    document.body.style.pointerEvents = 'auto'
  }, [])

  const handleCloseAutoFocus = useCallback(
    (e: Event) => {
      e.preventDefault()
      setTimeout(() => {
        const restoreFocusElement = onCloseFocusRef?.current || lastFocusedElement
        if (restoreFocusElement) {
          restoreFocusElement.focus()
        }
        document.body.style.pointerEvents = 'auto'
      })
    },
    [lastFocusedElement, onCloseFocusRef]
  )

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.stopPropagation()
      onClose()
    },
    [onClose]
  )

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      onClose()
    },
    [onClose]
  )

  const handleInteractOutside = useCallback((e: Event) => e.preventDefault(), [])

  return (
    <DialogPrimitive.Root open onOpenChange={onClose} modal={false}>
      <DialogPrimitive.Portal container={getModalRoot()}>
        <Overlay onClick={handleOverlayClick} />
        <AlignmentContainer>
          <Panel
            $height={height}
            $width={width}
            aria-describedby=""
            onAnimationEnd={handleAnimationEnd}
            onCloseAutoFocus={handleCloseAutoFocus}
            onEscapeKeyDown={handleEscapeKeyDown}
            onInteractOutside={handleInteractOutside}
            className={className}
          >
            <ModalContext.Provider value={{ onClose }}>{children}</ModalContext.Provider>
          </Panel>
        </AlignmentContainer>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

type ModalContentProps = {
  className?: string
  children: React.ReactNode
}
const ModalContent = ({ children, className }: ModalContentProps) => {
  return (
    <Main className={className} onClick={stopPropagation}>
      {typeof children === 'string' ? <T.Body>{children}</T.Body> : children}
    </Main>
  )
}

type ModalFooterProps =
  | {
      cancelText?: never
      children: React.ReactNode
      confirmText?: never
      isDestructive?: never
      isLoading?: never
      disabledReason?: never
      onCancel?: never
      onConfirm?: never
    }
  | {
      cancelText?: string
      children?: never
      confirmText?: string
      isDestructive?: boolean
      isLoading?: boolean
      disabledReason?: string
      onCancel?: () => void
      onConfirm?: (() => void) | (() => Promise<unknown>)
    }
const ModalFooter = forwardRef<HTMLElement, ModalFooterProps>(
  (
    {
      children,
      cancelText,
      confirmText,
      isDestructive,
      isLoading,
      disabledReason,
      onCancel,
      onConfirm,
    }: ModalFooterProps,
    ref
  ) => {
    const { onClose } = useModalContext()
    const { isOpen: isConfirming, open: startConfirming, close: stopConfirming } = useDisclosure()
    const handleConfirm = useCallback(async () => {
      if (isConfirming) {
        return
      }

      try {
        startConfirming()
        await onConfirm?.()
        onClose()
      } finally {
        stopConfirming()
      }
    }, [isConfirming, onClose, onConfirm, startConfirming, stopConfirming])

    return (
      <Footer ref={ref} onClick={stopPropagation}>
        {children || ref ? (
          children
        ) : (
          <>
            <Button onClick={onCancel ?? onClose}>{cancelText ?? 'Cancel'}</Button>
            <Tooltip content={disabledReason}>
              <Button
                type="primary"
                onClick={handleConfirm}
                danger={isDestructive}
                loading={isLoading || isConfirming}
                disabled={!!disabledReason}
              >
                {confirmText ?? 'Confirm'}
              </Button>
            </Tooltip>
          </>
        )}
      </Footer>
    )
  }
)

type ModalHeaderProps = {
  children: React.ReactNode
  icon?: IconDefinition
}
const ModalHeader = ({ children, icon }: ModalHeaderProps) => {
  const { onClose } = useModalContext()

  return (
    <Header onClick={stopPropagation}>
      <Flex
        justify="space-between"
        align="baseline"
        style={{
          // This guarantees that the close button is the first focus target
          flexDirection: 'row-reverse',
        }}
      >
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faXmark} />
        </CloseButton>
        <Title>
          <Flex align="baseline" gap="8px">
            {icon && (
              <T.BodyLarge className="text-orange-600">
                <FontAwesomeIcon icon={icon} />
              </T.BodyLarge>
            )}
            {typeof children === 'string' ? (
              <T.BodyLarge className="font-semibold">{children}</T.BodyLarge>
            ) : (
              children
            )}
          </Flex>
        </Title>
      </Flex>
    </Header>
  )
}

type ModalContextType = {
  onClose: () => void
}
const ModalContext = React.createContext({} as ModalContextType)
const useModalContext = () => useContext(ModalContext)

export default Object.assign(Modal, {
  Content: ModalContent,
  Footer: ModalFooter,
  Header: ModalHeader,
})
